import { isApiError, isBearerRequiredError } from '../services/apiError'

/**
 * Nothing measured it, so show that rather than a confident default.
 *
 * One declaration, imported wherever a readout can be absent. A blank cell and a
 * measured zero must never look alike, and three private copies of this constant
 * is how they start to.
 */
export const NOT_MEASURED = '—'

/**
 * The API's RecordCounterCaveats.FinalAttemptOnly, for the surfaces that read a record count with no
 * served caveat beside it — the activity log's RecordsProcessed (Live Activity's Job History, the
 * Overview's Top Entities), which EtlActivityLogFactory writes from the FINAL attempt's counters.
 * The Explorer and Execution History serve the sentence per row; these read a count that cannot.
 */
export const FINAL_ATTEMPT_ONLY =
  "A resumed entity run's count describes only its final attempt: each attempt restarts its totals, "
  + 'so records loaded by earlier attempts are counted nowhere.'

/** Formatting helpers for operational readouts. */

/** "18.7M", "24.2K", "981" — for magnitudes where exactness is not the point. */
export function compactNumber(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 10_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

/** "1,284,003" — for values a reader may need to compare digit by digit. */
export function exactNumber(n: number): string {
  return n.toLocaleString()
}

/** "00:04:32" — durations line up in columns, so always three fields. */
export function duration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds))
  const hh = Math.floor(s / 3600)
  const mm = Math.floor((s % 3600) / 60)
  const ss = s % 60
  return [hh, mm, ss].map((v) => String(v).padStart(2, '0')).join(':')
}

/** "4m 32s" — compact form for inline use. */
export function shortDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds))
  if (s < 60) return `${s}s`
  const mm = Math.floor(s / 60)
  const ss = s % 60
  if (mm < 60) return ss ? `${mm}m ${ss}s` : `${mm}m`
  const hh = Math.floor(mm / 60)
  return `${hh}h ${mm % 60}m`
}

/**
 * "12m ago", "2h ago", "3d ago".
 *
 * Relative rather than absolute because the reader's question is "how stale is
 * this", not "what time was it" — and the header already shows the wall clock and
 * its timezone if they need to convert.
 */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return NOT_MEASURED
  const seconds = Math.round((Date.now() - then) / 1000)
  // A FUTURE instant used to fall into "just now" — every negative number is
  // below 45 — so "doubtful until 21:15" read as if 21:15 had already passed.
  // /schedules-load is the first page to show instants ahead of the clock; the
  // small window either side of zero stays "just now" so clock skew between the
  // API and the browser does not print "in 0m".
  if (seconds < -45) {
    const ahead = -seconds
    const minutesAhead = Math.round(ahead / 60)
    if (minutesAhead < 60) return `in ${minutesAhead}m`
    const hoursAhead = Math.round(minutesAhead / 60)
    if (hoursAhead < 24) return `in ${hoursAhead}h`
    return `in ${Math.round(hoursAhead / 24)}d`
  }
  if (seconds < 45) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

/**
 * An absolute timestamp with its relative form in parentheses, or a dash.
 *
 * Both halves, because the two questions are different and a reader usually has
 * both: "how stale is this" is answered by the relative form, and "was that
 * before or after the thing I am holding" needs the wall clock.
 *
 * Null never becomes "never" — a timestamp nobody recorded and an event that
 * never happened are different findings, and only the server knows which this is.
 */
export function whenLabel(iso: string | null): string {
  if (iso == null) return NOT_MEASURED
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return NOT_MEASURED
  return `${parsed.toLocaleString()} (${relativeTime(iso)})`
}

/**
 * The compact relative form alone, or a dash. Null never becomes "never".
 *
 * Note for callers: the returned string ALREADY CARRIES "ago" ("12m ago"), so it
 * completes a sentence rather than filling a preposition slot — "published
 * {agoLabel(x)}" reads correctly and "in {agoLabel(x)}" does not. A future
 * instant comes back as "in 12m", which completes the same sentences.
 */
export function agoLabel(iso: string | null): string {
  return iso == null ? NOT_MEASURED : relativeTime(iso)
}

/**
 * A served C# `DateTime` as an ISO string that parses as UTC.
 *
 * The ReportBuilder tables' instants are `DateTime`s written as UTC that SQL Server hands back with
 * no kind, so the wire carries no offset (`2026-09-01T12:00:00`) and `new Date` reads it as LOCAL
 * time — off by the browser's UTC offset. A date-time with no offset gets a `Z`; one with a `Z` or
 * an offset, and a bare date, are returned unchanged. (governanceFormat.utcIso and
 * registryFormat.asUtc apply the same rule to their own surfaces.)
 */
export function utcFromServer(iso: string): string {
  const value = iso.trim()
  if (/(?:z|[+-]\d{2}:?\d{2})$/i.test(value)) return value
  return /T\d{2}:\d{2}/.test(value) ? `${value}Z` : value
}

/** {@link whenLabel} of a served `DateTime` read as UTC ({@link utcFromServer}); a null stays a dash. */
export function whenUtcLabel(iso: string | null): string {
  return whenLabel(iso == null ? null : utcFromServer(iso))
}

/** Seconds since an ISO timestamp — drives the live elapsed timers. */
export function elapsedSeconds(iso: string): number {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 0
  return Math.max(0, (Date.now() - then) / 1000)
}

/**
 * The first line of an error, capped.
 *
 * `fetchApi` puts a non-JSON error body into the message whole, and in
 * Development an unhandled exception can come back as the developer exception
 * page in plain text, whose later sections can list the request's own headers —
 * the API key or bearer token among them. So a page that prints an error prints
 * its first line and no more.
 *
 * Born in `configurationFormat.ts` and moved here when `/schedules-load` became
 * its second caller: a helper two features use does not live in the first
 * one's folder.
 */
export function errorSummary(error: unknown): string | null {
  if (!(error instanceof Error) || !error.message) return null
  const first = error.message.split(/\r?\n/, 1)[0].trim()
  return first.length > 200 ? `${first.slice(0, 200)}…` : first
}

/**
 * The HTTP status a failed call came back with, or null when there was none —
 * a network failure, or an error that never reached the server.
 *
 * Imports only `services/apiError.ts`, never `services/api.ts`: this module must
 * stay importable without MSAL, which reads `window` at module load.
 */
export function errorStatus(error: unknown): number | null {
  return isApiError(error) ? error.status : null
}

/** What kind of failure an error is — the branch a page renders, not the text it prints. */
export type ErrorKind =
  | 'sign-in-required'
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'bad-request'
  | 'conflict'
  | 'server'
  | 'network'
  | 'unknown'

/**
 * Classifies a query or mutation error so loading, restricted, missing and broken
 * look different.
 *
 * `sign-in-required` comes first and never came from a server: a bearer-only
 * request (the two ETL writes) was refused BEFORE it was sent because this
 * session has no Entra token (`BearerRequiredError`). A 403 is `forbidden` — the
 * caller lacks a role, which is a state to explain, not a fault to paint red. A
 * rejected `fetch` (the server unreachable, CORS, offline) surfaces as a
 * `TypeError` and is `network`. Anything else without a status is `unknown`.
 *
 * Every switch over this type keeps a `default`, so a kind added here never
 * falls through silently in a caller that does not know it.
 */
export function errorKind(error: unknown): ErrorKind {
  if (isBearerRequiredError(error)) return 'sign-in-required'
  const status = errorStatus(error)
  if (status !== null) {
    if (status === 400) return 'bad-request'
    if (status === 401) return 'unauthorized'
    if (status === 403) return 'forbidden'
    if (status === 404) return 'not-found'
    if (status === 409) return 'conflict'
    if (status >= 500) return 'server'
    return 'unknown'
  }
  if (error instanceof TypeError) return 'network'
  return 'unknown'
}

/**
 * The one sentence every Lighthouse.Admin-gated surface uses when the server says
 * the caller does not hold the role — the rail's dimmed hint, the restricted
 * state's title, the notification. One declaration so they cannot drift apart.
 */
export const ADMIN_ROLE_REQUIRED = 'Needs the Lighthouse.Admin role'

/**
 * What a surface shows where the server withheld recorded text from this caller — a halt's detail, or an error
 * that copies it (D8). Not NOT_MEASURED and never blank: something WAS recorded, and only an administrator may read
 * it. Shown dimmed, and decided by the server's `…Withheld` flag, never by the caller's role.
 */
export const DETAIL_ADMIN_ONLY = 'Detail visible to administrators'

/** "Resolving Keys" from "ResolvingKeys". */
export function humanizePhase(phase: string): string {
  return phase.replace(/([a-z])([A-Z])/g, '$1 $2')
}
